import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Eye,
  EyeOff,
  ClipboardCopy,
  Check
} from 'lucide-react'
import { IntegrationIcon } from '@/components/integrations/IntegrationIcon'
import { CayuKeysSelector } from '@/components/integrations/CayuKeysSelector'
import { CayuKeysBadge } from '@/components/integrations/CayuKeysBadge'
import { SwitchKeysDialogs } from '@/components/integrations/SwitchKeysDialogs'
import { SetupInstructions } from '@/components/integrations/SetupInstructions'
import type { Integration, IntegrationTemplate, Project } from '@/types/conversations'

interface IntegrationCardProps {
  integrationType: string
  projectIntegration?: Integration
  orgIntegration?: Integration
  isExpanded: boolean
  onToggleExpand: () => void
  usingOrgIntegration: boolean
  projectId: number
  environment: 'sandbox' | 'live'
  project: Project
}

// Static template data for when no integration exists
const TEMPLATE_DATA: Record<string, IntegrationTemplate> = {
  openai: {
    name: "OpenAI",
    description: "AI models and language processing",
    icon: "bot",
    cayu_providable: true,
    documentation_url: "https://platform.openai.com/docs/api-reference/introduction",
    deep_links: [{ label: "Open API Keys", url: "https://platform.openai.com/api-keys" }],
    environment_variables: [
      { key: "OPENAI_API_KEY", label: "API Key", description: "Your OpenAI API key", required: true, placeholder: "sk-...", sensitive: true, validation_pattern: "^sk-", validation_hint: "Must start with sk-" },
      { key: "OPENAI_ORGANIZATION_ID", label: "Organization ID", description: "Your OpenAI organization ID (optional)", required: false, placeholder: "org-...", sensitive: false, validation_pattern: "^org-", validation_hint: "Must start with org-" }
    ]
  },
  anthropic: {
    name: "Anthropic",
    description: "Claude AI models and APIs",
    icon: "brain",
    cayu_providable: true,
    documentation_url: "https://docs.anthropic.com/claude/reference/getting-started-with-the-api",
    deep_links: [{ label: "Open Console", url: "https://console.anthropic.com/settings/keys" }],
    environment_variables: [
      { key: "ANTHROPIC_API_KEY", label: "API Key", description: "Your Anthropic API key", required: true, placeholder: "sk-ant-...", sensitive: true, validation_pattern: "^sk-ant-", validation_hint: "Must start with sk-ant-" }
    ]
  },
  google_gemini: {
    name: "Google Gemini",
    description: "Google's AI models and APIs",
    icon: "sparkles",
    cayu_providable: true,
    documentation_url: "https://ai.google.dev/gemini-api/docs",
    deep_links: [{ label: "Get API Key", url: "https://aistudio.google.com/apikey" }],
    environment_variables: [
      { key: "GEMINI_API_KEY", label: "API Key", description: "Your Google AI Studio API key", required: true, placeholder: "AI...", sensitive: true, validation_pattern: "^AI", validation_hint: "Must start with AI" }
    ]
  },
  stripe: {
    name: "Stripe",
    description: "Accept payments and manage subscriptions",
    icon: "credit-card",
    documentation_url: "https://stripe.com/docs/keys",
    deep_links: [{ label: "Open API Keys", url: "https://dashboard.stripe.com/apikeys" }],
    environment_variables: [
      { key: "STRIPE_PUBLIC_KEY", label: "Publishable Key", description: "Your Stripe publishable key (starts with pk_)", required: true, placeholder: "pk_test_...", sensitive: false, validation_pattern: "^pk_(test|live)_", validation_hint: "Must start with pk_test_ or pk_live_" },
      { key: "STRIPE_SECRET_KEY", label: "Secret Key", description: "Your Stripe secret key (starts with sk_)", required: true, placeholder: "sk_test_...", sensitive: true, validation_pattern: "^sk_(test|live)_", validation_hint: "Must start with sk_test_ or sk_live_" },
      { key: "STRIPE_WEBHOOK_SECRET", label: "Webhook Secret", description: "Your Stripe webhook signing secret (starts with whsec_)", required: false, placeholder: "whsec_...", sensitive: true, validation_pattern: "^whsec_", validation_hint: "Must start with whsec_" }
    ]
  },
  resend: {
    name: "Resend",
    description: "Send transactional emails",
    icon: "mail",
    documentation_url: "https://resend.com/docs/api-reference/introduction",
    deep_links: [{ label: "Open API Keys", url: "https://resend.com/api-keys" }],
    environment_variables: [
      { key: "RESEND_API_KEY", label: "API Key", description: "Your Resend API key", required: true, placeholder: "re_...", sensitive: true, validation_pattern: "^re_", validation_hint: "Must start with re_" }
    ]
  },
  twilio: {
    name: "Twilio",
    description: "Send SMS and make phone calls",
    icon: "phone",
    documentation_url: "https://www.twilio.com/docs/usage/api",
    deep_links: [{ label: "Open Console", url: "https://console.twilio.com" }],
    environment_variables: [
      { key: "TWILIO_ACCOUNT_SID", label: "Account SID", description: "Your Twilio Account SID", required: true, placeholder: "AC...", sensitive: false, validation_pattern: "^AC", validation_hint: "Must start with AC" },
      { key: "TWILIO_AUTH_TOKEN", label: "Auth Token", description: "Your Twilio Auth Token", required: true, placeholder: "your-auth-token", sensitive: true },
      { key: "TWILIO_PHONE_NUMBER", label: "Phone Number", description: "Your Twilio phone number", required: true, placeholder: "+1234567890", sensitive: false, validation_pattern: "^\\+", validation_hint: "Must start with + (E.164 format)" }
    ]
  },
  sendgrid: {
    name: "SendGrid",
    description: "Email delivery and marketing platform",
    icon: "mail",
    cayu_providable: true,
    documentation_url: "https://docs.sendgrid.com/api-reference/api-keys/create-api-keys",
    deep_links: [{ label: "Open API Keys", url: "https://app.sendgrid.com/settings/api_keys" }],
    environment_variables: [
      { key: "SENDGRID_API_KEY", label: "API Key", description: "Your SendGrid API key", required: true, placeholder: "SG...", sensitive: true, validation_pattern: "^SG\\.", validation_hint: "Must start with SG." },
      { key: "MAILER_FROM_ADDRESS", label: "From Address", description: "Email address that appears in the 'From' field (must be verified in SendGrid)", required: true, placeholder: "noreply@yourdomain.com", sensitive: false }
    ]
  },
  aws_s3: {
    name: "AWS S3",
    description: "Cloud object storage",
    icon: "cloud",
    documentation_url: "https://docs.aws.amazon.com/AmazonS3/latest/userguide/",
    deep_links: [{ label: "Open IAM Console", url: "https://console.aws.amazon.com/iam" }],
    environment_variables: [
      { key: "AWS_ACCESS_KEY_ID", label: "Access Key ID", description: "Your AWS Access Key ID", required: true, placeholder: "AKIA...", sensitive: false, validation_pattern: "^AKIA", validation_hint: "Must start with AKIA" },
      { key: "AWS_SECRET_ACCESS_KEY", label: "Secret Access Key", description: "Your AWS Secret Access Key", required: true, placeholder: "your-secret-key", sensitive: true },
      { key: "AWS_REGION", label: "Region", description: "AWS region for your S3 bucket", required: true, placeholder: "us-east-1", sensitive: false },
      { key: "AWS_S3_BUCKET", label: "Bucket Name", description: "Your S3 bucket name", required: true, placeholder: "my-bucket", sensitive: false }
    ]
  },
  google_oauth: {
    name: "Google OAuth",
    description: "Sign in with Google",
    icon: "log-in",
    documentation_url: "https://developers.google.com/identity/protocols/oauth2",
    deep_links: [{ label: "Open Credentials", url: "https://console.cloud.google.com/apis/credentials" }],
    environment_variables: [
      { key: "GOOGLE_CLIENT_ID", label: "Client ID", description: "Your Google OAuth Client ID", required: true, placeholder: "your-client-id.apps.googleusercontent.com", sensitive: false, validation_pattern: "\\.apps\\.googleusercontent\\.com$", validation_hint: "Must end with .apps.googleusercontent.com" },
      { key: "GOOGLE_CLIENT_SECRET", label: "Client Secret", description: "Your Google OAuth Client Secret", required: true, placeholder: "your-client-secret", sensitive: true }
    ]
  },
  github_oauth: {
    name: "GitHub OAuth",
    description: "Sign in with GitHub",
    icon: "github",
    documentation_url: "https://docs.github.com/en/apps/oauth-apps/building-oauth-apps",
    deep_links: [{ label: "Open Developer Settings", url: "https://github.com/settings/developers" }],
    environment_variables: [
      { key: "GITHUB_CLIENT_ID", label: "Client ID", description: "Your GitHub OAuth App Client ID", required: true, placeholder: "Iv1...", sensitive: false },
      { key: "GITHUB_CLIENT_SECRET", label: "Client Secret", description: "Your GitHub OAuth App Client Secret", required: true, placeholder: "your-client-secret", sensitive: true }
    ]
  },
  samsara: {
    name: "Samsara",
    description: "Fleet telematics and truck tracking",
    icon: "truck",
    documentation_url: "https://developers.samsara.com/docs",
    deep_links: [{ label: "Open Settings", url: "https://cloud.samsara.com/settings" }],
    environment_variables: [
      { key: "SAMSARA_API_KEY", label: "API Key", description: "Your Samsara API key for fleet telematics", required: true, placeholder: "samsara_api_...", sensitive: true, validation_pattern: "^samsara_api_", validation_hint: "Must start with samsara_api_" }
    ]
  },
  wex: {
    name: "WEX",
    description: "Fuel card and fleet payment services",
    icon: "credit-card",
    documentation_url: "https://developer.wexinc.com/",
    deep_links: [{ label: "Open WEX Portal", url: "https://developer.wexinc.com/" }],
    environment_variables: [
      { key: "WEX_USERNAME", label: "Username", description: "Your WEX account username", required: true, placeholder: "your-wex-username", sensitive: false },
      { key: "WEX_PASSWORD", label: "Password", description: "Your WEX account password", required: true, placeholder: "your-wex-password", sensitive: true },
      { key: "WEX_CARD_NUMBER", label: "Card Number", description: "Your WEX fleet card number", required: true, placeholder: "1234567890123456", sensitive: true }
    ]
  },
  here_maps: {
    name: "HERE Maps",
    description: "Maps, routing, and location services",
    icon: "map",
    documentation_url: "https://developer.here.com/documentation",
    deep_links: [{ label: "Open HERE Portal", url: "https://developer.here.com/projects" }],
    environment_variables: [
      { key: "HERE_API_KEY", label: "API Key", description: "Your HERE Maps API key", required: true, placeholder: "your-here-api-key", sensitive: true }
    ]
  },
  google_maps: {
    name: "Google Maps",
    description: "Maps, Places, and Geocoding APIs",
    icon: "map-pin",
    documentation_url: "https://developers.google.com/maps/documentation",
    deep_links: [{ label: "Open Credentials", url: "https://console.cloud.google.com/apis/credentials" }],
    environment_variables: [
      { key: "GOOGLE_MAPS_API_KEY", label: "API Key", description: "Your Google Maps API key", required: true, placeholder: "AIza...", sensitive: true, validation_pattern: "^AIza", validation_hint: "Must start with AIza" }
    ]
  },
  telegram: {
    name: "Telegram",
    description: "Bot messaging and notifications",
    icon: "send",
    documentation_url: "https://core.telegram.org/bots/api",
    deep_links: [{ label: "Open BotFather", url: "https://t.me/BotFather" }],
    environment_variables: [
      { key: "TELEGRAM_BOT_TOKEN", label: "Bot Token", description: "Your Telegram bot token from @BotFather", required: true, placeholder: "123456:ABC-DEF...", sensitive: true, validation_pattern: "^\\d+:", validation_hint: "Must be in format: numbers:letters (e.g., 123456:ABC-DEF)" }
    ]
  },
  instagram: {
    name: "Instagram",
    description: "Instagram Business API with OAuth and webhooks",
    icon: "instagram",
    documentation_url: "https://developers.facebook.com/docs/instagram-platform",
    deep_links: [{ label: "Open Meta Developers", url: "https://developers.facebook.com/apps" }],
    environment_variables: [
      { key: "INSTAGRAM_APP_ID", label: "App ID", description: "Your Meta App ID", required: true, placeholder: "123456789012345", sensitive: false },
      { key: "INSTAGRAM_APP_SECRET", label: "App Secret", description: "Your Meta App Secret", required: true, placeholder: "your-app-secret", sensitive: true },
      { key: "INSTAGRAM_CALLBACK_URL", label: "Callback URL", description: "OAuth redirect URI (auto-generated from app domain)", required: false, placeholder: "https://your-app.cayu.app/api/auth/instagram/callback/", sensitive: false, auto_generate: "callback_url", auto_generate_path: "/api/auth/instagram/callback/" },
      { key: "INSTAGRAM_WEBHOOK", label: "Webhook URL", description: "Webhook endpoint (auto-generated from app domain)", required: false, placeholder: "https://your-app.cayu.app/api/webhook/instagram/", sensitive: false, auto_generate: "webhook_url", auto_generate_path: "/api/webhook/instagram/" },
      { key: "INSTAGRAM_VERIFY_TOKEN", label: "Verify Token", description: "Webhook verification token (auto-generated if not provided)", required: false, placeholder: "your_verify_token", sensitive: true, auto_generate: "random_token" },
      { key: "INSTAGRAM_EMBED_URL", label: "OAuth URL", description: "OAuth authorization URL (auto-generated from App ID and Callback URL)", required: false, placeholder: "https://www.instagram.com/oauth/authorize?...", sensitive: false }
    ]
  },
  whatsapp: {
    name: "WhatsApp",
    description: "WhatsApp Business Cloud API with webhooks",
    icon: "message-circle",
    documentation_url: "https://developers.facebook.com/docs/whatsapp/cloud-api",
    deep_links: [{ label: "Open Meta Developers", url: "https://developers.facebook.com/apps" }],
    environment_variables: [
      { key: "WHATSAPP_APP_ID", label: "App ID", description: "Your Meta App ID", required: true, placeholder: "123456789012345", sensitive: false },
      { key: "WHATSAPP_APP_SECRET", label: "App Secret", description: "Your Meta App Secret", required: true, placeholder: "your-app-secret", sensitive: true },
      { key: "WHATSAPP_ACCESS_TOKEN", label: "Access Token", description: "Permanent WhatsApp Cloud API access token", required: true, placeholder: "EAAxxxxxxx...", sensitive: true, validation_pattern: "^EAA", validation_hint: "Must start with EAA" },
      { key: "WHATSAPP_PHONE_NUMBER_ID", label: "Phone Number ID", description: "WhatsApp Business Phone Number ID", required: true, placeholder: "123456789012345", sensitive: false },
      { key: "WHATSAPP_CALLBACK_URL", label: "Callback URL", description: "OAuth redirect URI (auto-generated from app domain)", required: false, placeholder: "https://your-app.cayu.app/api/auth/whatsapp/callback/", sensitive: false, auto_generate: "callback_url", auto_generate_path: "/api/auth/whatsapp/callback/" },
      { key: "WHATSAPP_WEBHOOK", label: "Webhook URL", description: "Webhook endpoint (auto-generated from app domain)", required: false, placeholder: "https://your-app.cayu.app/api/webhook/whatsapp/", sensitive: false, auto_generate: "webhook_url", auto_generate_path: "/api/webhook/whatsapp/" },
      { key: "WHATSAPP_VERIFY_TOKEN", label: "Verify Token", description: "Webhook verification token (auto-generated if not provided)", required: false, placeholder: "your_verify_token", sensitive: true, auto_generate: "random_token" }
    ]
  },
  youtube: {
    name: "YouTube",
    description: "Video management and analytics",
    icon: "play-circle",
    documentation_url: "https://developers.google.com/youtube/v3/docs",
    deep_links: [{ label: "Open Credentials", url: "https://console.cloud.google.com/apis/credentials" }],
    environment_variables: [
      { key: "YOUTUBE_API_KEY", label: "API Key", description: "Your Google YouTube Data API key", required: true, placeholder: "AIza...", sensitive: true, validation_pattern: "^AIza", validation_hint: "Must start with AIza" },
      { key: "YOUTUBE_CHANNEL_ID", label: "Channel ID", description: "Your YouTube channel ID (optional for some operations)", required: false, placeholder: "UCxxxxxxxxxxxxxx", sensitive: false, validation_pattern: "^UC", validation_hint: "Must start with UC" }
    ]
  }
}

// Generate a random token for auto-generated fields
function generateRandomToken(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

// Compute auto-generated env var values based on project URL
function getAutoGeneratedValues(template: IntegrationTemplate, projectUrl?: string): Record<string, string> {
  const values: Record<string, string> = {}
  if (!template.environment_variables) return values

  for (const envVar of template.environment_variables) {
    if (!envVar.auto_generate) continue
    if (envVar.auto_generate === 'random_token') {
      values[envVar.key] = generateRandomToken()
    } else if ((envVar.auto_generate === 'callback_url' || envVar.auto_generate === 'webhook_url') && projectUrl && envVar.auto_generate_path) {
      const baseUrl = projectUrl.replace(/\/$/, '')
      values[envVar.key] = `${baseUrl}${envVar.auto_generate_path}`
    }
  }
  return values
}

// Check if a value matches a validation pattern
function validateKeyFormat(value: string, pattern?: string): boolean | null {
  if (!pattern || !value) return null
  try {
    return new RegExp(pattern).test(value)
  } catch {
    return null
  }
}

export function IntegrationCard({
  integrationType,
  projectIntegration,
  orgIntegration,
  isExpanded,
  onToggleExpand,
  usingOrgIntegration,
  projectId,
  environment,
  project
}: IntegrationCardProps) {
  const { t } = useTranslation('conversations')
  const [envVars, setEnvVars] = useState<Record<string, string>>({})
  const [autoGenerated, setAutoGenerated] = useState<Set<string>>(new Set())
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [saveToOrg, setSaveToOrg] = useState(false)
  const [visibleValues, setVisibleValues] = useState<Set<number>>(new Set())
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false)
  const [showDisconnectLiveDialog, setShowDisconnectLiveDialog] = useState(false)
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  // Cayu keys state
  const [useCayuKeys, setUseCayuKeys] = useState(false)
  const [showSwitchToOwnKeysDialog, setShowSwitchToOwnKeysDialog] = useState(false)
  const [showSwitchToCayuKeysDialog, setShowSwitchToCayuKeysDialog] = useState(false)

  // Get template from integration or fallback to static template data
  const template = projectIntegration?.template || TEMPLATE_DATA[integrationType]

  // Auto-fill generated URLs when card expands and integration is not connected
  const hasAutoFilled = useRef(false)
  useEffect(() => {
    if (isExpanded && !projectIntegration && template && !hasAutoFilled.current) {
      const generated = getAutoGeneratedValues(template, project.starter_app_url || undefined)
      if (Object.keys(generated).length > 0) {
        hasAutoFilled.current = true
        setEnvVars(prev => {
          const updated = { ...prev }
          for (const [key, value] of Object.entries(generated)) {
            if (!prev[key]) updated[key] = value
          }
          return updated
        })
        setAutoGenerated(new Set(Object.keys(generated)))
      }
    }
  }, [isExpanded, projectIntegration, template, project.starter_app_url])

  const handleCopyToClipboard = (key: string, value: string) => {
    navigator.clipboard.writeText(value)
    setCopiedKey(key)
    setTimeout(() => setCopiedKey(null), 2000)
  }

  // Get environment-specific variables
  const environmentVariables = environment === 'sandbox'
    ? projectIntegration?.sandbox_variables || []
    : projectIntegration?.live_variables || []

  // Check if this environment has variables configured OR is using Cayu keys (sandbox only)
  const hasEnvironmentVariables = environmentVariables.length > 0 ||
    (environment === 'sandbox' && projectIntegration?.using_cayu_keys)

  // Status badge
  const getStatusBadge = () => {
    // For live environment, check if live variables exist
    if (environment === 'live') {
      if (!projectIntegration || !hasEnvironmentVariables) {
        return <Badge variant="secondary" className="text-xs">{t('integrations.status.notConnected')}</Badge>
      }
      return (
        <Badge variant="default" className="text-xs bg-green-600">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          {t('integrations.status.connected')}
        </Badge>
      )
    }

    // For sandbox environment, use the old logic
    if (!projectIntegration) {
      return <Badge variant="secondary" className="text-xs">{t('integrations.status.notConnected')}</Badge>
    }

    switch (projectIntegration.status) {
      case 'connected':
        return (
          <Badge variant="default" className="text-xs bg-green-600">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            {t('integrations.status.connected')}
          </Badge>
        )
      case 'error':
        return (
          <Badge variant="destructive" className="text-xs">
            <XCircle className="h-3 w-3 mr-1" />
            {t('integrations.status.error')}
          </Badge>
        )
      default:
        return <Badge variant="secondary" className="text-xs">{t('integrations.status.disconnected')}</Badge>
    }
  }

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault()

    if (isSubmitting) return

    setIsSubmitting(true)

    try {
      const requestBody: any = {
        integration_type: integrationType,
        save_to_organization: saveToOrg,
        is_live: environment === 'live',
        use_cayu_keys: useCayuKeys
      }

      // Only include environment_variables if NOT using Cayu keys
      if (!useCayuKeys) {
        requestBody.environment_variables = envVars
      }

      const response = await fetch(`/projects/${projectId}/integrations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify(requestBody)
      })

      const data = await response.json()

      if (response.ok && data.success) {
        toast.success(data.message || t('integrations.toasts.connectSuccess'))
        setEnvVars({})
        setUseCayuKeys(false)
        router.reload()
      } else {
        const errorMessage = data.errors ? data.errors.join(', ') : t('integrations.toasts.connectError')
        toast.error(errorMessage)
      }
    } catch (error) {
      console.error('Error connecting integration:', error)
      toast.error(t('integrations.toasts.unexpectedError'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const toggleValueVisibility = (id: number) => {
    setVisibleValues(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleDisconnectClick = () => {
    setShowDisconnectDialog(true)
  }

  const handleConfirmDisconnect = async () => {
    if (!projectIntegration) return

    setShowDisconnectDialog(false)
    setIsSubmitting(true)

    try {
      const response = await fetch(`/projects/${projectId}/integrations/${projectIntegration.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      })

      const data = await response.json()

      if (response.ok && data.success) {
        toast.success(data.message || t('integrations.toasts.disconnectSuccess'))
        setIsSubmitting(false)
        router.reload()
      } else {
        const errorMessage = data.errors ? data.errors.join(', ') : t('integrations.toasts.disconnectError')
        toast.error(errorMessage)
        setIsSubmitting(false)
      }
    } catch (error) {
      console.error('Error disconnecting integration:', error)
      toast.error(t('integrations.toasts.unexpectedError'))
      setIsSubmitting(false)
    }
  }

  const handleDisconnectLiveClick = () => {
    setShowDisconnectLiveDialog(true)
  }

  const handleConfirmDisconnectLive = async () => {
    if (!projectIntegration) return

    setShowDisconnectLiveDialog(false)
    setIsSubmitting(true)

    try {
      const response = await fetch(`/projects/${projectId}/integrations/${projectIntegration.id}/disconnect_live`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      })

      const data = await response.json()

      if (response.ok && data.success) {
        toast.success(data.message || t('integrations.toasts.disconnectLiveSuccess'))
        setIsSubmitting(false)
        router.reload()
      } else {
        const errorMessage = data.errors ? data.errors.join(', ') : t('integrations.toasts.disconnectLiveError')
        toast.error(errorMessage)
        setIsSubmitting(false)
      }
    } catch (error) {
      console.error('Error disconnecting live credentials:', error)
      toast.error(t('integrations.toasts.unexpectedError'))
      setIsSubmitting(false)
    }
  }

  const handleImportFromOrg = async () => {
    if (!orgIntegration) return

    setIsSubmitting(true)

    try {
      const response = await fetch(`/projects/${projectId}/integrations/import_from_organization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({
          integration_type: integrationType
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        toast.success(data.message || t('integrations.toasts.importOrgSuccess'))
        router.reload()
      } else {
        const errorMessage = data.errors ? data.errors.join(', ') : t('integrations.toasts.importOrgError')
        toast.error(errorMessage)
      }
    } catch (error) {
      console.error('Error importing integration:', error)
      toast.error(t('integrations.toasts.unexpectedError'))
    } finally {
      setIsSubmitting(false)
    }
  }


  return (
    <div className="border rounded-lg bg-card overflow-hidden">
      {/* Card Header - Always Visible */}
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-accent transition-colors"
      >
        <div className="flex items-center gap-3">
          <IntegrationIcon integrationType={integrationType} />
          <div className="text-left">
            <h3 className="font-semibold text-sm text-foreground">
              {template?.name || integrationType}
            </h3>
            <p className="text-xs text-muted-foreground">
              {template?.description || t('integrations.fallback.description')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {usingOrgIntegration && (
            <Badge variant="outline" className="text-xs">
              {t('integrations.status.usingOrg')}
            </Badge>
          )}
          {projectIntegration?.using_cayu_keys && (
            <CayuKeysBadge />
          )}
          {getStatusBadge()}
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t p-4 bg-muted/50">
          {/* Documentation Link */}
          {template?.documentation_url && (
            <a
              href={template.documentation_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mb-4"
            >
              {t('integrations.docs')}
              <ExternalLink className="h-3 w-3" />
            </a>
          )}

          {/* Error Message */}
          {projectIntegration?.status === 'error' && projectIntegration.last_error && environment === 'sandbox' && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-800">{projectIntegration.last_error}</p>
            </div>
          )}

          {/* Not Connected - Show Connection Form or Import Option (Sandbox only) */}
          {!hasEnvironmentVariables && template && environment === 'sandbox' && (
            <div className="space-y-4">
              {/* Show import button if org integration exists */}
              {orgIntegration && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-xs text-blue-800 mb-3">
                    {t('integrations.org.available')}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleImportFromOrg}
                    className="w-full"
                  >
                    {t('integrations.org.import')}
                  </Button>
                </div>
              )}

              {/* Setup Instructions - Show when not connected */}
              <SetupInstructions
                integrationType={integrationType}
                isConnected={false}
                projectUrl={project.starter_app_url || undefined}
                deepLinks={template?.deep_links}
                copyableUrls={
                  project.starter_app_url
                    ? template?.environment_variables
                        .filter(ev => ev.auto_generate === 'callback_url' || ev.auto_generate === 'webhook_url')
                        .map(ev => ({
                          label: ev.label,
                          value: `${project.starter_app_url!.replace(/\/$/, '')}${ev.auto_generate_path || ''}`
                        }))
                    : undefined
                }
              />

              {/* Connection Form */}
              <form onSubmit={handleConnect} className="space-y-4">
                {/* Cayu Keys Selector - only show for new connections in sandbox */}
                {template?.cayu_providable && project.available_cayu_services?.includes(integrationType) && !hasEnvironmentVariables && environment === 'sandbox' && (
                  <CayuKeysSelector
                    onUseCayuKeysChange={(usesCayu) => setUseCayuKeys(usesCayu)}
                  />
                )}

                {/* Secrets - only show if NOT using Cayu keys */}
                {!useCayuKeys && (
                  <div className="space-y-3">
                    {template.environment_variables.map((envVar) => {
                      const value = envVars[envVar.key] || ''
                      const isAutoGen = autoGenerated.has(envVar.key)
                      const validationResult = validateKeyFormat(value, envVar.validation_pattern)

                      return (
                        <div key={envVar.key} className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Label htmlFor={`${integrationType}-${envVar.key}`} className="text-sm">
                              {envVar.label}
                              {envVar.required && <span className="text-red-600 ml-1">*</span>}
                            </Label>
                            {isAutoGen && (
                              <span className="text-[10px] font-medium text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded">
                                {t('integrations.autoGenerated', { defaultValue: 'Auto-generated' })}
                              </span>
                            )}
                          </div>
                          <div className="relative flex items-center gap-1">
                            <div className="relative flex-1">
                              <Input
                                id={`${integrationType}-${envVar.key}`}
                                type={envVar.sensitive && !isAutoGen ? 'password' : 'text'}
                                placeholder={envVar.placeholder}
                                value={value}
                                onChange={(e) => {
                                  setEnvVars({ ...envVars, [envVar.key]: e.target.value })
                                  if (isAutoGen) {
                                    setAutoGenerated(prev => {
                                      const next = new Set(prev)
                                      next.delete(envVar.key)
                                      return next
                                    })
                                  }
                                }}
                                readOnly={isAutoGen}
                                required={envVar.required}
                                className={`font-mono text-sm pr-8 ${isAutoGen ? 'bg-muted' : ''}`}
                              />
                              {/* Validation indicator */}
                              {validationResult !== null && value.length > 0 && (
                                <span className="absolute right-2 top-1/2 -translate-y-1/2">
                                  {validationResult ? (
                                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                                  )}
                                </span>
                              )}
                            </div>
                            {/* Copy button for auto-generated URL fields */}
                            {isAutoGen && value && (
                              <button
                                type="button"
                                onClick={() => handleCopyToClipboard(envVar.key, value)}
                                className="flex-shrink-0 p-2 text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-accent"
                                title={t('integrations.copyUrl', { defaultValue: 'Copy to clipboard' })}
                              >
                                {copiedKey === envVar.key ? (
                                  <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                  <ClipboardCopy className="h-4 w-4" />
                                )}
                              </button>
                            )}
                          </div>
                          {/* Validation hint */}
                          {validationResult === false && value.length > 0 && envVar.validation_hint && (
                            <p className="text-xs text-amber-600">{envVar.validation_hint}</p>
                          )}
                          {envVar.description && (
                            <p className="text-xs text-muted-foreground">{envVar.description}</p>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Save to Organization Checkbox */}
                <div className="flex items-center space-x-2 p-3 bg-muted rounded-md">
                  <input
                    type="checkbox"
                    id={`save-to-org-${integrationType}`}
                    checked={saveToOrg}
                    onChange={(e) => setSaveToOrg(e.target.checked)}
                    className="h-4 w-4 rounded border-input text-primary focus:ring-2 focus:ring-primary"
                  />
                  <Label htmlFor={`save-to-org-${integrationType}`} className="cursor-pointer text-sm text-foreground">
                    {t('integrations.org.saveCheckbox')}
                  </Label>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? t('integrations.buttons.connecting') : t('integrations.buttons.connect')}
                </Button>
              </form>
            </div>
          )}

          {/* Not Connected - Live tab message */}
          {!hasEnvironmentVariables && environment === 'live' && (
            <div className="text-center py-6 bg-muted rounded-md border border-dashed">
              <p className="text-sm text-muted-foreground mb-2">
                {t('integrations.live.noCredentials')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('integrations.live.useImport')}
              </p>
            </div>
          )}

          {/* Connected - Show Secrets */}
          {hasEnvironmentVariables && template && (
            <div className="space-y-4">
              {/* Setup Instructions - Collapsible when connected */}
              {environment === 'sandbox' && (
                <SetupInstructions
                  integrationType={integrationType}
                  isConnected={true}
                  projectUrl={project.starter_app_url || undefined}
                  deepLinks={template?.deep_links}
                />
              )}

              {/* Secrets Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-foreground">
                    {t('integrations.secrets.title')} {environment === 'live' && <span className="text-xs font-normal text-muted-foreground ml-2">{t('integrations.secrets.readOnly')}</span>}
                  </h4>
                  <span className="text-xs text-muted-foreground">
                    {t('integrations.secrets.count', { count: environmentVariables.length })}
                  </span>
                </div>
                {environmentVariables.length === 0 ? (
                  <div className="text-center py-6 bg-card rounded-md border border-dashed">
                    <p className="text-sm text-muted-foreground">
                      {projectIntegration?.using_cayu_keys
                        ? t('integrations.secrets.cayuManaged')
                        : t('integrations.secrets.noSecrets')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {environmentVariables.map((envVar) => {
                      const templateVar = template.environment_variables.find(
                        v => v.key === envVar.key
                      )
                      return (
                        <div
                          key={envVar.id}
                          className="bg-card border rounded-md p-3"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <code className="text-xs font-semibold text-foreground bg-muted px-2 py-0.5 rounded">
                                  {envVar.key}
                                </code>
                                {templateVar?.required && (
                                  <span className="text-xs text-red-600 font-medium">*</span>
                                )}
                              </div>
                              {templateVar?.label && (
                                <div className="text-xs text-foreground/80 mb-1">
                                  {templateVar.label}
                                </div>
                              )}
                              {(envVar.description || templateVar?.description) && (
                                <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                                  {envVar.description || templateVar?.description}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-1 rounded break-all max-w-[300px]">
                                {visibleValues.has(envVar.id) ? envVar.value : '••••••••'}
                              </span>
                              <button
                                type="button"
                                onClick={() => toggleValueVisibility(envVar.id)}
                                className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                              >
                                {visibleValues.has(envVar.id) ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Switch buttons - only in sandbox environment */}
              {environment === 'sandbox' && projectIntegration && template?.cayu_providable && (
                <div className="flex gap-2">
                  {/* Switch to Own Keys button - show if using Cayu keys */}
                  {projectIntegration.using_cayu_keys && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSwitchToOwnKeysDialog(true)}
                      className="flex-1"
                    >
                      {t('integrations.buttons.switchToOwnKeys')}
                    </Button>
                  )}

                  {/* Switch to Cayu Keys button - show if using own keys AND service supports it */}
                  {!projectIntegration.using_cayu_keys && project.available_cayu_services?.includes(integrationType) && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSwitchToCayuKeysDialog(true)}
                      className="flex-1"
                    >
                      {t('integrations.buttons.switchToCayuKeys')}
                    </Button>
                  )}
                </div>
              )}

              {/* Disconnect buttons */}
              {environment === 'sandbox' && projectIntegration && (
                <Button type="button" variant="destructive" onClick={handleDisconnectClick} className="w-full">
                  {t('integrations.buttons.disconnect')}
                </Button>
              )}
              {environment === 'live' && projectIntegration && hasEnvironmentVariables && (
                <Button type="button" variant="destructive" onClick={handleDisconnectLiveClick} className="w-full">
                  {t('integrations.buttons.disconnectLive')}
                </Button>
              )}
            </div>
          )}

        </div>
      )}

      {/* Disconnect Sandbox Dialog */}
      <AlertDialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('integrations.dialogs.disconnectTitle')}</AlertDialogTitle>
            <AlertDialogDescription dangerouslySetInnerHTML={{
              __html: t('integrations.dialogs.disconnectDescription', { name: template?.name || integrationType })
            }} />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('integrations.dialogs.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDisconnect} className="bg-red-600 hover:bg-red-700">
              {t('integrations.buttons.disconnect')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Disconnect Live Dialog */}
      <AlertDialog open={showDisconnectLiveDialog} onOpenChange={setShowDisconnectLiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('integrations.dialogs.disconnectLiveTitle')}</AlertDialogTitle>
            <AlertDialogDescription dangerouslySetInnerHTML={{
              __html: t('integrations.dialogs.disconnectLiveDescription', { name: template?.name || integrationType })
            }} />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('integrations.dialogs.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDisconnectLive} className="bg-red-600 hover:bg-red-700">
              {t('integrations.buttons.disconnect')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Switch Keys Dialogs */}
      {projectIntegration && template?.cayu_providable && (
        <SwitchKeysDialogs
          projectId={projectId}
          integrationId={projectIntegration.id}
          environmentVariables={template.environment_variables || []}
          showSwitchToOwnKeys={showSwitchToOwnKeysDialog}
          showSwitchToCayuKeys={showSwitchToCayuKeysDialog}
          onCloseSwitchToOwnKeys={() => setShowSwitchToOwnKeysDialog(false)}
          onCloseSwitchToCayuKeys={() => setShowSwitchToCayuKeysDialog(false)}
        />
      )}
    </div>
  )
}
